import React from 'react';
import { getHsvHoldingStructure } from '../hsvCorpData';
import {
  ReactFlow,
  Handle,
  Position,
  MarkerType,
  BaseEdge,
  getStraightPath
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './react-flow.scss';

// --- CUSTOM EDGES ---

const StraightHorizontalEdge = ({ sourceX, sourceY, targetX, style, markerEnd }) => {
  const [edgePath] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY: sourceY,
  });
  return <BaseEdge path={edgePath} style={style} markerEnd={markerEnd} />;
};

const StraightVerticalEdge = ({ sourceX, sourceY, targetY, style, markerEnd }) => {
  const [edgePath] = getStraightPath({
    sourceX,
    sourceY,
    targetX: sourceX,
    targetY,
  });
  return <BaseEdge path={edgePath} style={style} markerEnd={markerEnd} />;
};

const edgeTypes = {
  straightHorizontal: StraightHorizontalEdge,
  straightVertical: StraightVerticalEdge,
};

// --- CUSTOM NODES ---

const TopNode = ({ data }) => (
  <div className="rf-top-node">
    {data.targetHandle && <Handle type="target" position={Position.Left} id="left" />}
    {data.label}
    {data.sourceHandle && <Handle type="source" position={Position.Right} id="right" />}
    {data.bottomHandle && <Handle type="source" position={Position.Bottom} id="bottom" />}
  </div>
);

const CeoNode = ({ data }) => (
  <div className="rf-ceo-node">
    <Handle type="target" position={Position.Left} id="left" />
    {data.label}
    <Handle type="source" position={Position.Right} id="right" />
    <Handle type="source" position={Position.Bottom} id="bottom" />
  </div>
);

const DepartmentNode = ({ data }) => (
  <div className="rf-dept-node">
    <Handle type="target" position={Position.Top} id="top" />
    <div className="dept-name">{data.name}</div>
    <div className="dept-tasks">
      {data.tasks.map((t, i) => <div key={i} className="task-item">{t}</div>)}
    </div>
    <Handle type="source" position={Position.Bottom} id="bottom" />
  </div>
);

const CategoryNode = ({ data }) => (
  <div className="rf-cat-node">
    <div className="cat-title">{data.category}</div>
    <div className="cat-items">
      {data.items.map((item, i) => (
        <div key={i} className="cat-item">
          {item}
        </div>
      ))}
    </div>
    <Handle type="source" position={Position.Right} id="right" style={{ top: '50%' }} />
  </div>
);

const HospitalGroupNode = ({ data }) => (
  <div className="rf-hospital-group">
    <Handle type="target" id="target-top" position={Position.Top} style={{ left: '50%', opacity: 0 }} />
    <Handle type="target" id="target-left" position={Position.Left} style={{ top: '50%', opacity: 0 }} />

    <div className="hospitals-container">
      {data.hospitals.map((hosp, i) => (
        <div key={i} className={`hospital-bar ${hosp.size}`}>
          <div className="hosp-name">{hosp.name}</div>
          <div className="bar-fill"></div>
        </div>
      ))}
    </div>
  </div>
);


const nodeTypes = {
  topNode: TopNode,
  ceoNode: CeoNode,
  deptNode: DepartmentNode,
  catNode: CategoryNode,
  hospitalGroup: HospitalGroupNode,
};

const defaultEdgeOptions = {
  style: { stroke: '#10b981', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
};

export default function HsvMatrixReactFlow({ isEnglish }) {
  const holdingStruct = getHsvHoldingStructure(isEnglish);
  const structData = holdingStruct?.structuralForm;

  const generatedNodes = React.useMemo(() => {
    if (!structData || !structData.topLevel) return [];

    return [
      { id: 'bod', type: 'topNode', position: { x: 0, y: 0 }, data: { label: structData.topLevel[0], sourceHandle: true } },
      { id: 'ceo', type: 'ceoNode', position: { x: 250, y: 0 }, data: { label: structData.topLevel[1] } },
      { id: 'strategy', type: 'topNode', position: { x: 500, y: 0 }, data: { label: structData.topLevel[2], targetHandle: true, sourceHandle: true } },
      { id: 'csr', type: 'topNode', position: { x: 700, y: 0 }, data: { label: structData.topLevel[3], targetHandle: true } },

      ...structData.departments.map((dept, i) => ({
        id: `dept-${i}`,
        type: 'deptNode',
        position: { x: 280 + i * 200, y: 150 },
        data: { name: dept.name, tasks: dept.tasks }
      })),

      ...structData.leftMatrix.map((cat, i) => {
        const yPos = [400, 510, 670, 830][i] || 400 + i * 150;
        return {
          id: `cat-${i}`,
          type: 'catNode',
          position: { x: 0, y: yPos },
          data: { category: cat.category, items: cat.items }
        };
      }),

      { 
        id: 'hospitals', 
        type: 'hospitalGroup', 
        position: { x: 280, y: 380 }, 
        style: { width: 1000, height: 550, zIndex: -1 },
        data: { hospitals: structData.hospitals } 
      }
    ];
  }, [structData]);

  const generatedEdges = React.useMemo(() => {
    if (!structData || !structData.topLevel) return [];
    
    const edges = [
      { id: 'e-bod-ceo', source: 'bod', target: 'ceo', sourceHandle: 'right', targetHandle: 'left', type: 'straight', ...defaultEdgeOptions },
      { id: 'e-ceo-strat', source: 'ceo', target: 'strategy', sourceHandle: 'right', targetHandle: 'left', type: 'straight', ...defaultEdgeOptions },
      { id: 'e-strat-csr', source: 'strategy', target: 'csr', sourceHandle: 'right', targetHandle: 'left', type: 'straight', ...defaultEdgeOptions },
    ];

    structData.departments.forEach((_, i) => {
      edges.push({
        id: `e-ceo-d${i}`,
        source: 'ceo',
        target: `dept-${i}`,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'step',
        style: { stroke: '#10b981', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' }
      });
    });

    structData.departments.forEach((_, i) => {
      edges.push({
        id: `e-d${i}-h`,
        source: `dept-${i}`,
        target: 'hospitals',
        sourceHandle: 'bottom',
        targetHandle: 'target-top',
        type: 'straightVertical',
        ...defaultEdgeOptions
      });
    });

    structData.leftMatrix.forEach((_, i) => {
      edges.push({
        id: `e-c${i}-h`,
        source: `cat-${i}`,
        target: 'hospitals',
        sourceHandle: 'right',
        targetHandle: 'target-left',
        type: 'straightHorizontal',
        ...defaultEdgeOptions
      });
    });

    return edges;
  }, [structData]);

  return (
    <div className="hsv-flow-mobile-scroll-container">
      <div className="hsv-flow-scroll-hint">
        👈 {isEnglish ? 'Swipe to view diagram' : 'Vuốt/Kéo ngang để xem tiếp'} 👉
      </div>

      <div className="hsv-react-flow-wrapper">
        <ReactFlow
          nodes={generatedNodes}
          edges={generatedEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={false}
          panOnScroll={false}
          panOnDrag={true}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          fitView
          fitViewOptions={{ padding: 0.1, minZoom: 0.8, maxZoom: 1 }}
        />
      </div>
    </div>
  );
}
